import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { IDevicesResponse, fetchDevices } from '../../../../libraries/helpdesk/devices';
import { useEffect, useState } from 'react';
import Header from '../../../components/util/header';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import { Group, Loader, Table } from '@mantine/core';
import PaginationComponent from '../../../components/pagination';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import './styles.scss';

const HelpdeskDevices = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const controller = new AbortController();

  //const [searchParams,  setSearchParams ] = useSearchParams();
  const [list, setList] = useState<IDevicesResponse>();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRowsResponse] = useState<number | undefined>();
  const [noResults, setNoResults] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [statusOperCode, setStatusOperCode] = useState<number | undefined>();
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: -2,
    minHeightForDynamic: 400
  });

  //const eidParam = searchParams.get('eid');
  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();

  useEffect(() => {
    if (filterBarData && limit) {
      loadDevices();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadDevices = () => {
    if (loading) {
      setNoResults(false);
      controller.abort();
    }
    setLoading(true);
    fetchDevices(
      controller,
      filterBarData?.devId,
      filterBarData?.eid,
      filterBarData?.sn,
      filterBarData?.codArm,
      filterBarData?.productCode
    )
      .then(response => {
        if (response.statusOper.code === 0) {
          setList(response);
          setNoResults(false);
          setStatusOperCode(response.statusOper.code);
        } else {
          setNoResults(true);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  return (
    <div className="devices">
      <Header pageTitle="Devices" />

      <HelpdeskFilterBar
        onSearch={loadDevices}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          eid: true,
          sn: true,
          devId: true,
          codArm: true,
          productCode: true
        }}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>DevID</Table.Th>
              <Table.Th>SN</Table.Th>
              <Table.Th>{lang.HELPDESK_PRODUCT_CODE}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_ID_MIN}</Table.Th>
              <Table.Th>{lang.GLOBAL_WAREHOUSE}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={12}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {noResults ? (
                  <Table.Tr>
                    <Table.Td colSpan={12} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.devices.map((device, index) => (
                    <Table.Tr key={index}>
                      <Table.Td>{device.devID}</Table.Td>
                      <Table.Td>{device.sn}</Table.Td>
                      <Table.Td>{device.productCode}</Table.Td>
                      <Table.Td>{device.eid}</Table.Td>
                      <Table.Td>{device.codArm}</Table.Td>
                    </Table.Tr>
                  ))
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRowsResponse}
          statusOperCode={statusOperCode}
          itemsPerPage={limit}
          dynamicLimit={limit}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          currentPage={currentPage}
        />
      </Group>
    </div>
  );
};

export default HelpdeskDevices;
